import React, { createContext, useContext, useCallback, useRef, useState } from 'react';
import { FeedbackCountEntry, FeedbackEntityType, getFeedbackCounts } from '../api/feedbackApi';

// Batches per-card feedback-count lookups into one bulk request per feed
// render pass — same discipline as PublishStatusContext (a TEND view mounts
// ~45 cards at once; per-card fetches saturate the connection pool).

interface Waiter {
  key: string;
  resolve: (entry: FeedbackCountEntry) => void;
}

interface FeedbackCountsContextType {
  /** Batched + cached count — safe to call from every card on mount. */
  getCount: (brandId: number, entityType: FeedbackEntityType, entityKey: string) => Promise<FeedbackCountEntry>;
  /** Local adjustment after a post/delete inside the panel — no refetch. */
  bump: (brandId: number, entityType: FeedbackEntityType, entityKey: string, delta: number) => void;
  /** Local resolved-state flip after resolve/reopen in the panel. */
  setResolved: (brandId: number, entityType: FeedbackEntityType, entityKey: string, resolved: boolean) => void;
  /** Subscribe-free read of the latest known entries (render helper). */
  counts: Record<string, FeedbackCountEntry>;
}

const FeedbackCountsContext = createContext<FeedbackCountsContextType | null>(null);

const BATCH_WINDOW_MS = 50;
const cacheKey = (brandId: number, entityType: string, entityKey: string) =>
  `${brandId}:${entityType}:${entityKey}`;

export const FeedbackCountsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [counts, setCounts] = useState<Record<string, FeedbackCountEntry>>({});
  const countsRef = useRef(counts);
  countsRef.current = counts;

  const batchRef = useRef<Map<string, Waiter[]>>(new Map());
  const timerRef = useRef<Map<string, number>>(new Map());

  const flush = useCallback(async (batchId: string) => {
    timerRef.current.delete(batchId);
    const waiters = batchRef.current.get(batchId) || [];
    batchRef.current.delete(batchId);
    if (waiters.length === 0) return;
    const [brandIdStr, entityType] = batchId.split('|');
    const brandId = Number(brandIdStr);
    const keys = Array.from(new Set(waiters.map((w) => w.key)));
    try {
      const result = await getFeedbackCounts(entityType as FeedbackEntityType, keys, brandId);
      setCounts((prev) => {
        const next = { ...prev };
        for (const key of keys) {
          next[cacheKey(brandId, entityType, key)] = result[key] || { count: 0, resolved: false };
        }
        return next;
      });
      waiters.forEach((w) => w.resolve(result[w.key] || { count: 0, resolved: false }));
    } catch {
      waiters.forEach((w) => w.resolve({ count: 0, resolved: false }));
    }
  }, []);

  const getCount = useCallback(
    (brandId: number, entityType: FeedbackEntityType, entityKey: string): Promise<FeedbackCountEntry> => {
      const cached = countsRef.current[cacheKey(brandId, entityType, entityKey)];
      if (cached !== undefined) return Promise.resolve(cached);
      return new Promise((resolve) => {
        const batchId = `${brandId}|${entityType}`;
        const waiters = batchRef.current.get(batchId) || [];
        waiters.push({ key: entityKey, resolve });
        batchRef.current.set(batchId, waiters);
        if (!timerRef.current.has(batchId)) {
          timerRef.current.set(
            batchId,
            window.setTimeout(() => flush(batchId), BATCH_WINDOW_MS)
          );
        }
      });
    },
    [flush]
  );

  const bump = useCallback(
    (brandId: number, entityType: FeedbackEntityType, entityKey: string, delta: number) => {
      const key = cacheKey(brandId, entityType, entityKey);
      setCounts((prev) => {
        const cur = prev[key] || { count: 0, resolved: false };
        // A new comment also auto-reopens the thread (server does the same).
        return {
          ...prev,
          [key]: {
            count: Math.max(0, cur.count + delta),
            resolved: delta > 0 ? false : cur.resolved,
          },
        };
      });
    },
    []
  );

  const setResolved = useCallback(
    (brandId: number, entityType: FeedbackEntityType, entityKey: string, resolved: boolean) => {
      const key = cacheKey(brandId, entityType, entityKey);
      setCounts((prev) => ({
        ...prev,
        [key]: { count: (prev[key] || { count: 0, resolved: false }).count, resolved },
      }));
    },
    []
  );

  return (
    <FeedbackCountsContext.Provider value={{ getCount, bump, setResolved, counts }}>
      {children}
    </FeedbackCountsContext.Provider>
  );
};

export const useFeedbackCounts = (): FeedbackCountsContextType => {
  const ctx = useContext(FeedbackCountsContext);
  if (!ctx) throw new Error('useFeedbackCounts must be used within FeedbackCountsProvider');
  return ctx;
};

export { cacheKey as feedbackCacheKey };
